import { z } from 'zod'

// Statuts du devis selon le workflow STORY-018
export const devisStatusEnum = z.enum([
  'draft',      // Brouillon - admin cree le devis
  'sent',       // Envoye - admin envoie au client
  'viewed',     // Vu - client ouvre le lien
  'accepted',   // Accepte - client signe
  'rejected',   // Refuse - client decline
  'expired',    // Expire - date de validite depassee
  'converted',  // Facture - converti en facture
])

export type DevisStatus = z.infer<typeof devisStatusEnum>

export const ligneDevisSchema = z.object({
  id: z.string(),
  description: z.string().min(1, 'La description est requise'),
  quantite: z.number().min(1, 'La quantite doit etre superieure a 0'),
  prix_unitaire: z.number().min(0, 'Le prix doit etre positif'),
  taux_tva: z.number().default(0),
  total_ht: z.number(),
})

export const devisSchema = z.object({
  id: z.string().uuid().optional(),
  numero: z.string().min(1, 'Le numero est requis'),
  contact_id: z.string().uuid('Contact requis'),
  projet_id: z.string().uuid().nullable().optional(),
  titre: z.string().min(1, 'Le titre est requis'),
  description: z.string().nullable().optional(),
  date_emission: z.string(),
  date_validite: z.string(),
  validite_jours: z.number().default(30),
  status: devisStatusEnum.default('draft'),
  montant_ht: z.number().default(0),
  taux_tva: z.number().default(0),
  montant_tva: z.number().default(0),
  montant_ttc: z.number().default(0),
  conditions: z.string().nullable().optional(),
  notes_internes: z.string().nullable().optional(),
  lignes: z.array(ligneDevisSchema).default([]),
  // Tracking et signature
  share_token: z.string().nullable().optional(),
  date_vue: z.string().nullable().optional(),
  date_reponse: z.string().nullable().optional(),
  signature_client: z.string().nullable().optional(),
  signature_date: z.string().nullable().optional(),
  ip_signature: z.string().nullable().optional(),
  message_client: z.string().nullable().optional(),
  // Lien vers facture si converti
  facture_id: z.string().uuid().nullable().optional(),
})

export type LigneDevis = z.infer<typeof ligneDevisSchema>

export type Devis = z.infer<typeof devisSchema> & {
  id: string
  created_at: string
  updated_at: string
  qonto_id?: string | null
  qonto_url?: string | null
  contact?: {
    id: string
    full_name?: string
    nom?: string
    prenom?: string
    email: string
    entreprise?: string
    company?: string
  }
}

export type DevisFormData = z.infer<typeof devisSchema>

export interface DevisFilters {
  search?: string
  status?: DevisStatus
  dateFrom?: string
  dateTo?: string
  contactId?: string
}

export const devisStatusLabels: Record<DevisStatus, string> = {
  draft: 'Brouillon',
  sent: 'Envoye',
  viewed: 'Vu',
  accepted: 'Accepte',
  rejected: 'Refuse',
  expired: 'Expire',
  converted: 'Facture',
}

export const devisStatusColors: Record<DevisStatus, string> = {
  draft: 'bg-beige-100 text-beige-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-purple-100 text-purple-800',
  accepted: 'bg-mint-100 text-mint-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-brown-100 text-brown-800',
  converted: 'bg-gold-100 text-gold-800',
}

export interface SignatureData {
  signature: string
  accepted_conditions: boolean
  timestamp: string
  ip: string
  typed_name?: string
}

export interface DevisStats {
  total: number
  en_cours: number
  taux_conversion: number
  montant_en_attente: number
}

// Pour la page publique
export interface PublicDevisResponse {
  devis: Devis | null
  error?: string
  isExpired?: boolean
}

// Transitions de statut valides
export const devisStatusTransitions: Record<DevisStatus, DevisStatus[]> = {
  draft: ['sent'],
  sent: ['viewed', 'accepted', 'rejected', 'expired'],
  viewed: ['accepted', 'rejected', 'expired'],
  accepted: ['converted'],
  rejected: [],
  expired: [],
  converted: [],
}
